import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSite } from "@/contexts/SiteContext";
import Marquee from "react-fast-marquee";
import { ShoppingBag, Heart, User, Menu, X, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
    { to: "/shop", label: "Shop" },
    { to: "/shop?gender=men", label: "Men" },
    { to: "/shop?gender=women", label: "Women" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

const DEFAULT_NAV = [
    { label: "Shop", link: "/shop" },
    { label: "Men", link: "/shop?gender=men" },
    { label: "Women", link: "/shop?gender=women" },
    { label: "About", link: "/about" },
    { label: "Contact", link: "/contact" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { cart, wishlist } = useCart();
    const { settings } = useSite();
    const [banner, setBanner] = useState(null);
    const [config, setConfig] = useState({});
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const loc = useLocation();
    const NAV = (settings?.nav_menu && settings.nav_menu.length) ? settings.nav_menu : DEFAULT_NAV;

    useEffect(() => {
        api.get("/sale/active").then(r => setBanner(r.data)).catch(() => {});
        api.get("/config").then(r => setConfig(r.data)).catch(() => {});
    }, [loc.pathname]);

    // GA4
    useEffect(() => {
        if (config.ga4_measurement_id && !window.__ga_loaded) {
            const s = document.createElement("script");
            s.async = true; s.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4_measurement_id}`;
            document.head.appendChild(s);
            window.dataLayer = window.dataLayer || [];
            window.gtag = function(){ window.dataLayer.push(arguments); };
            window.gtag('js', new Date());
            window.gtag('config', config.ga4_measurement_id);
            window.__ga_loaded = true;
        }
    }, [config.ga4_measurement_id]);

    const cartCount = cart.items.reduce((s, i) => s + i.quantity, 0);

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="app-shell">
            {settings?.announcement_enabled && settings?.announcement_text && (
                settings?.announcement_link ? (
                    <a href={settings.announcement_link} className="block bg-black text-white text-center py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-neutral-900" data-testid="announcement-bar">
                        {settings.announcement_text}
                    </a>
                ) : (
                    <div className="bg-black text-white text-center py-2 font-mono text-[11px] uppercase tracking-widest" data-testid="announcement-bar">
                        {settings.announcement_text}
                    </div>
                )
            )}
            {banner && (
                <Link to={`/shop?sale=${banner.id}`} data-testid="sale-banner-link" className="block text-white border-b-2 border-black" style={{ background: "hsl(var(--accent))" }}>
                    <Marquee gradient={false} speed={45} pauseOnHover className="py-2 font-mono text-xs uppercase tracking-widest">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <span className="tick-item" key={i}>▶ {banner.headline} — Up to {banner.discount_percent}% Off  ●  Shop Now  ●  </span>
                        ))}
                    </Marquee>
                </Link>
            )}
            <header className="sticky top-0 z-40 bg-white border-b-2 border-black">
                <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 md:px-10 h-16">
                    <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
                        {settings?.logo_url ? (
                            <img src={settings.logo_url} alt={settings?.store_name || "Clonzo"} className="h-8 md:h-10 w-auto object-contain" />
                        ) : (
                            <span className="font-display text-2xl md:text-3xl font-bold tracking-tighter">{(settings?.store_name || "CLONZO").toUpperCase()}</span>
                        )}
                    </Link>
                    <nav className="hidden md:flex gap-8 font-mono text-xs uppercase tracking-widest">
                        {NAV.map(n => <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`} className="hover:text-[hsl(var(--accent))] transition-colors">{n.label}</Link>)}
                    </nav>
                    <div className="flex items-center gap-3 md:gap-5">
                        <Link to="/wishlist" data-testid="wishlist-link" className="relative">
                            <Heart className="w-5 h-5" />
                            {wishlist.items.length > 0 && <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] px-1 font-mono">{wishlist.items.length}</span>}
                        </Link>
                        <Link to="/cart" data-testid="cart-link" className="relative">
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[hsl(var(--accent))] text-white text-[10px] px-1 font-mono">{cartCount}</span>}
                        </Link>
                        {user ? (
                            <div className="hidden md:flex items-center gap-3">
                                <Link to={user.role === 'admin' ? '/admin' : '/orders'} data-testid="account-link" className="font-mono text-xs uppercase">{user.name.split(' ')[0]}</Link>
                                <button onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn" className="font-mono text-xs uppercase text-neutral-500 hover:text-[hsl(var(--accent))]">Logout</button>
                            </div>
                        ) : (
                            <Link to="/login" data-testid="login-link" className="hidden md:inline font-mono text-xs uppercase">Login</Link>
                        )}
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <button className="md:hidden" data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
                            </SheetTrigger>
                            <SheetContent side="right" className="border-l-2 border-black bg-white">
                                <div className="flex flex-col gap-6 mt-8 font-mono uppercase text-sm">
                                    {NAV.map(n => <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`m-nav-${n.label.toLowerCase()}`}>{n.label}</Link>)}
                                    <div className="border-t-2 border-black pt-6 flex flex-col gap-4">
                                        {user ? (
                                            <>
                                                <Link to={user.role === 'admin' ? '/admin' : '/orders'} onClick={() => setMobileOpen(false)}>My Account</Link>
                                                <button onClick={() => { logout(); setMobileOpen(false); navigate('/'); }} className="text-left">Logout</button>
                                            </>
                                        ) : (
                                            <>
                                                <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
                                                <Link to="/signup" onClick={() => setMobileOpen(false)}>Sign Up</Link>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>

            <main className="flex-1">{children}</main>

            <footer className="mt-24 bg-black text-white border-t-2 border-black">
                <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
                    <div className="col-span-2">
                        <div className="font-display text-4xl md:text-6xl tracking-tighter">{(settings?.store_name || "CLONZO").toUpperCase()}</div>
                        <p className="mt-4 max-w-md text-neutral-400 text-sm">{settings?.footer_tagline || "Print-on-demand streetwear from India. Made when you order."}</p>
                        {(settings?.social_instagram || settings?.social_twitter) && (
                            <div className="mt-4 flex gap-4 font-mono text-xs">
                                {settings?.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="hover:text-white">Instagram ↗</a>}
                                {settings?.social_twitter && <a href={settings.social_twitter} target="_blank" rel="noreferrer" className="hover:text-white">Twitter ↗</a>}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="font-mono text-xs uppercase mb-4 text-neutral-500">Shop</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/shop">All</Link></li>
                            <li><Link to="/shop?gender=men">Men</Link></li>
                            <li><Link to="/shop?gender=women">Women</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-mono text-xs uppercase mb-4 text-neutral-500">Help</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/faqs">FAQs</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                            <li><Link to="/shipping-policy">Shipping</Link></li>
                            <li><Link to="/refund-policy">Returns</Link></li>
                            <li><Link to="/terms">Terms</Link></li>
                            <li><Link to="/privacy">Privacy</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-neutral-800 py-4 text-center font-mono text-[10px] uppercase tracking-widest text-neutral-500">© {new Date().getFullYear()} {settings?.store_name || "Clonzo"} — Made in India</div>
            </footer>
        </div>
    );
}
